import { useAuth0 } from '@auth0/auth0-react';
import { Loading } from './components';
import ThemeConfig from './theme';
import Router from './routes';
import NavBar from './components/NavBar/NavBar';
import ErrorHandlerModal from './components/ErrorHandlerModal/ErrorHandlerModal';
import { isEmpty, isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getSessionDetails } from './utils/localStorage';
import { getSession, setSessionDetails, verifySession } from './actions/session';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useIdleTimer } from 'react-idle-timer';
import {
  ACCESS_DENIED,
  IDLE_TIMEOUT_MESSAGE,
  IDLE_TIMER_SYNC_TIMERS,
  IDLE_TIMER_THROTTLE,
  IDLE_TIMER_TIMEOUT,
  TOKEN_CHECK_TIME_INTERVAL,
  TOKEN_UPDATE_TIME_WINDOW,
} from './constants/constants';
import 'react-toastify/dist/ReactToastify.css';
import CommonFooter from 'components/CommonFooter';
import { searchContractSubscriptionDetails } from 'actions/client';
import { isTokenExpired } from 'constants/tokenValidity';
import { datadogRum } from '@datadog/browser-rum';
import useLocalStorageListener from 'helpers/useLocalStorageListener';
import corlFavIcon from 'assets/images/corl-logo-loading.png';
import { Helmet } from 'react-helmet';
import { datadogLogs } from '@datadog/browser-logs';
import { ACCESS_TOKEN, SESSION_DETAILS, ISAUTHENTICATED, CORL_TOKEN, SESSION_IN_PROGRESS } from './utils/localStorage';

const App = () => {
  const { isLoading, getAccessTokenSilently, logout } = useAuth0();
  const dispatch = useDispatch();
  let history = useHistory();

  const [showNavbar, setShowNavbar] = useState(true);
  const details = useSelector((state) => state.session.details);
  const contractLoading = useSelector((state) => state.client.contractLoading);
  const isVerifiedSession = useSelector((state) => state.session.isVerified);
  const latestContractConfiguration = useSelector((state) => state?.client?.latestContractConfiguration);
  const [isTokenExpire, setIsTokenExpire] = useState(false);

  const handleLogout = () => {
    dispatch({ type: 'RESET_APP_STATE' });
    window.localStorage.removeItem(SESSION_DETAILS);
    window.localStorage.removeItem(ACCESS_TOKEN);
    window.localStorage.removeItem(ISAUTHENTICATED);
    window.localStorage.removeItem(CORL_TOKEN);
    window.localStorage.removeItem(SESSION_IN_PROGRESS);
    logout({
      returnTo: window.location.origin + '/logout',
    });
  }

  const onIdle = () => {
    history.push({
      pathname: '/error',
      state: {
        idleErrorMessage: IDLE_TIMEOUT_MESSAGE,
        logoutUser: true,
      },
    });
  };

  useIdleTimer({
    onIdle,
    timeout: IDLE_TIMER_TIMEOUT,
    throttle: IDLE_TIMER_THROTTLE,
    crossTab: true,
    leaderElection: true,
    syncTimers: IDLE_TIMER_SYNC_TIMERS,
  });

  axios.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem('corl-token') || '';
      let isExpired = isTokenExpired(token);

      if (isExpired) {
        setIsTokenExpire(true);
        if (
          config?.url === '/app/usersession' &&
          (config?.method === 'post' || config?.method === 'get') &&
          window.localStorage.getItem('sessionInProgress') !== 'yes'
        ) {
          // Keep the old flow for reference
          // const data = {
          //   appName: process.env.REACT_APP_NAME,
          // };
          // await dispatch(
          //   getSession(data, getAccessTokenSilently, null, () => {
          //     history.push({
          //       pathname: '/unauthorized',
          //       state: {
          //         text: 'Your session is expired. You have been logged out. Please login again.',
          //       },
          //     });
          //   })
          // );
          setShowNavbar(true);
          handleLogout();
        } else if (config?.url === '/app/usersession' && (config?.method === 'post' || config?.method === 'get')) {
          return config;
        } else if (window.localStorage.getItem('sessionInProgress') === 'yes') {
          throw new axios.Cancel('cancel');
        } else {
          // Keep the old flow for reference
          // const data = {
          //   appName: process.env.REACT_APP_NAME,
          // };
          // await dispatch(
          //   getSession(data, getAccessTokenSilently, null, () => {
          //     history.push({
          //       pathname: '/unauthorized',
          //       state: {
          //         text: 'Your session is expired. You have been logged out. Please login again.',
          //       },
          //     });
          //   })
          // );
          setShowNavbar(true);
          handleLogout();
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    function (response) {
      if (response?.data?.ErrorCount > 0) {
        const errors = response?.data?.Errors;
        errors?.forEach((element) => {
          if (element?.Message === ACCESS_DENIED) {
            history.push({ pathname: '/access-denied' });
          }
        });
      }
      return response;
    },
    function (error) {
      let errors = error?.response?.data?.Errors;

      // Need to handle error response for both new and old APIs
      let errorList = errors?.map((err) => err?.Message) || [error?.response?.data?.Message];
      if (error.response.status === 401) {
        setShowNavbar(false);
        // Keep the old flow for reference
        // const data = {
        //   appName: process.env.REACT_APP_NAME,
        // };
        // await dispatch(
        //   getSession(data, getAccessTokenSilently, null, () => {
        //     history.push({
        //       pathname: '/unauthorized',
        //       state: {
        //         text: 'Your session is expired. You have been logged out. Please login again.',
        //       },
        //     });
        //   })
        // );
        handleLogout();
      } else if (error.response.status === 500) {
        if (errorList?.length > 0) {
          const item = errorList?.find((it) => it.toLowerCase().includes('session has expired') || it.toLowerCase().includes('unauthorized access'));
          if (item) {
            localStorage.removeItem('corl-token');
            setShowNavbar(true);
            history.push({
              pathname: '/error',
              state: {
                errors: errorList,
                logoutUser: true,
              },
            });
          }
        } else {
          setShowNavbar(true);
          history.push({
            pathname: '/error',
            state: {
              errors: errorList,
              logoutUser: false,
            },
          });
        }
      }
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    if (isNil(localStorage.getItem('corl-token'))) {
      setShowNavbar(false);
    } else {
      setShowNavbar(true);
    }
  }, [details, dispatch]);

  useEffect(() => {
    if (!isVerifiedSession && !isEmpty(details)) {
      dispatch(verifySession());
    }
  }, [dispatch, isVerifiedSession]);

  useEffect(() => {
    const sessionDetails = getSessionDetails();
    dispatch(setSessionDetails(sessionDetails));
  }, [dispatch]);

  useEffect(() => {
    if (!isEmpty(details) && isEmpty(latestContractConfiguration) && !contractLoading) {
      dispatch(searchContractSubscriptionDetails());
    }
  }, [dispatch, latestContractConfiguration, details]);

  useEffect(() => {
    const envName = process.env.REACT_APP_ENVIRONMENT;
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    const datadogSessionReplaySampleRate = parseInt(process.env.REACT_APP_SESSION_REPLAY_SAMPLE_RATE) || 0;
    const monitorProxyEndpoint = process.env.REACT_APP_MONITOR_PROXY_ENDPOINT;
    const version = process.env.REACT_APP_VERSION;

    datadogRum.init({
      applicationId: 'e18584ac-f3fe-489a-9e15-e5661809bb5f',
      clientToken: 'pubabcd0f5d88c51d868dd9616bc0744a53',
      site: 'datadoghq.com',
      service: 'corl-client-portal',
      env: envName,
      // Specify a version number to identify the deployed version of your application in Datadog
      version: version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: datadogSessionReplaySampleRate,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [apiEndpoint],
      forwardErrorsToLogs: true,
      proxy: monitorProxyEndpoint,
    });
    
    if (envName !== "DEVELOPMENT")
    {
      datadogLogs.init({
        clientToken: 'pubabcd0f5d88c51d868dd9616bc0744a53',
        applicationId: 'e18584ac-f3fe-489a-9e15-e5661809bb5f',
        site: 'datadoghq.com',
        service: 'corl-client-portal',
        sessionSampleRate: 100, 
        version: version,
        proxy: monitorProxyEndpoint,
        env: envName,
        version: version,
        allowedTracingUrls: [apiEndpoint],
        forwardErrorsToLogs: true,
        silentMultipleInit: true
      });
      const sessionDetails = getSessionDetails();
      if(sessionDetails) 
      {
        datadogLogs.setUserProperty('session', sessionDetails)
      }
    }


    const interval = setInterval(() => {
      //check the token expiry beforehand
      //if the token will be expiring in TOKEN_UPDATE_TIME_WINDOW,
      //Obtain a new token and create a new session
      const token = localStorage.getItem('corl-token') || '';
      const willBeExpired = isTokenExpired(token, TOKEN_UPDATE_TIME_WINDOW);

      if (willBeExpired) {
        // Keep the old flow for reference
        // const data = {
        //   appName: process.env.REACT_APP_NAME,
        // };
        // await dispatch(
        //   getSession(data, getAccessTokenSilently, null, () => {
        //     history.push({
        //       pathname: '/unauthorized',
        //       state: {
        //         text: 'Your session is expired. You have been logged out. Please login again.',
        //       },
        //     });
        //   })
        // );
        handleLogout();
      }
    }, TOKEN_CHECK_TIME_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, []);

  //Setup eventlistener for change in localstorgae value
  useLocalStorageListener('CORL_sessionDetails');

  if (isLoading) {
    return <Loading />;
  }

  return (
    <ThemeConfig>
      {isTokenExpire ? (
        <Loading />
      ) : (
        <div id="app" className="d-flex flex-column h-100">
          {showNavbar && <NavBar />}
          <div className="flex-grow-1 app-main-content">
            <Router />
          </div>
          {showNavbar && <CommonFooter />}
        </div>
      )}
      <ErrorHandlerModal />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        closeButton={false}
        theme="light"
        bodyClassName="toastBody"
      />
      <Helmet>
        <link rel="shortcut icon" href={corlFavIcon} />
      </Helmet>
    </ThemeConfig>
  );
};

export default App;
